import type { Dayjs } from "dayjs"
import dayjs from "dayjs"

export interface Approval {
  approved: boolean
  date: string
  user: string
  remark?: string
}

export interface BasePriceListArticle {
  _id: string
  description: string
  articleNumber?: string
  contractId?: string
  priceListId?: string
  price: number
}

// This is currently an alias a.t.m, in case there ever will be contract-specific articles in the future
export type ContractPriceListArticle = BasePriceListArticle

export interface RouteNumber {
  routeNumber: string
  remark?: string
}

export interface PriceList {
  _id: string
  description: string
  articleCategory: string
  startAt?: string
  endAt?: string
  approval?: Approval
  articles?: Array<ContractPriceListArticle>
  useForInspections?: boolean
  useForMalfunctions?: boolean
  useForActions?: boolean
  useHours?: boolean
  manuallyEnded?: boolean
  status: PriceListStatus
}

export interface Contract {
  _id: string
  organization: string
  supplier?: string
  description: string
  responsibleUser?: { _id: string; description: string }
  referenceNumber?: string
  startAt?: string
  endAt?: string
  includeZeroCosts: boolean
  priceLists?: Array<PriceList>
  assets: Array<string>
  routeNumbers?: Array<RouteNumber>
  status: ContractStatus
}

export enum ContractStatus {
  AWAITING_START_DATE = "AWAITING_START_DATE",
  EXPIRED = "EXPIRED",
  AWAITING_APPROVAL = "AWAITING_APPROVAL",
  NO_ACTIVE_PRICE_LISTS = "NO_ACTIVE_PRICE_LISTS",
  PARTIALLY_APPROVED = "PARTIALLY_APPROVED",
  APPROVED = "APPROVED",
  MANUALLY_ENDED = "MANUALLY_ENDED",
  DENIED = "DENIED",
  INVALID_FOR_TICKET = "INVALID_FOR_TICKET",
}

export enum PriceListStatus {
  AWAITING_START_DATE = "AWAITING_START_DATE",
  EXPIRED = "EXPIRED",
  AWAITING_APPROVAL = "AWAITING_APPROVAL",
  DENIED = "DENIED",
  APPROVED = "APPROVED",
  MANUALLY_ENDED = "MANUALLY_ENDED",
  INVALID_FOR_TICKET = "INVALID_FOR_TICKET",
}

export interface ContractUpdate {
  description?: string | undefined
  responsibleUser?: { _id: string; description: string }
  referenceNumber?: string
  startAt?: string
  endAt?: string
  includeZeroCosts: boolean
  assets?: Array<string>
}

export interface ContractCreate extends ContractUpdate {
  /** Can be used when a supplier creates a contract from his organization for another organization.\
   * If left empty the current organization will be used. */
  organization?: string
  supplier?: string
}

export type ContractGetOneResult = Omit<Contract, "assets"> & {
  assets: Array<Pick<Asset, "_id" | "key" | "description" | "category">>
}

export interface ContractsSummary {
  pendingContracts: number
}

export const useContractStore = defineStore("contract", () => {
  const baseUrl = "/contracts"
  const refreshSummaryKey = ref(0)

  const triggerSummaryRefresh = () => {
    refreshSummaryKey.value++
  }

  const storedContracts = ref<Array<Contract>>()
  const storedContractExpiredDate = ref<Dayjs | null>(null)

  const getStoredContracts = async (): Promise<Array<Contract>> => {
    const currentDate = dayjs()

    if (storedContracts.value && storedContractExpiredDate.value?.isBefore(currentDate)) {
      return storedContracts.value
    }

    const query = getUrlSearchParams({
      pagination: false,
    })

    const { data } = await useSamApi<PaginateResult<Contract>>(`${baseUrl}?${query}`)

    storedContracts.value = data.docs
    // Expire contracts after 1 hour so it is updated semi regularly.
    storedContractExpiredDate.value = currentDate.add(1, "hour")

    return data.docs
  }

  const getActualTicketDate = (ticket: Partial<Pick<Ticket, "startAt" | "date">>): Dayjs =>
    dayjs(ticket.startAt ?? ticket.date ?? new Date())

  const isTicketTypeAssignableToPriceList = (ticket: Partial<Pick<Ticket, "type">>, priceList: PriceList): boolean => {
    switch (ticket.type) {
      case TicketType.ACTION:
        return !!priceList.useForActions
      case TicketType.INSPECTION:
      case TicketType.NEN1010:
      case TicketType.NEN3140:
        return !!priceList.useForInspections
      case TicketType.MALFUNCTION:
        return !!priceList.useForMalfunctions
      default:
        return false
    }
  }

  const recalculatePriceListStatusForTicket = (
    priceList: PriceList,
    ticket: Partial<Pick<Ticket, "startAt" | "date" | "type">>,
  ): PriceListStatus => {
    const ticketDate = getActualTicketDate(ticket)

    if (priceList.manuallyEnded) {
      return PriceListStatus.MANUALLY_ENDED
    }

    if (!isTicketTypeAssignableToPriceList(ticket, priceList)) {
      return PriceListStatus.INVALID_FOR_TICKET
    }

    if (priceList.approval && !priceList.approval.approved) {
      return PriceListStatus.DENIED
    }

    if (priceList.startAt) {
      const priceListStartDate = dayjs(priceList.startAt)

      if (priceListStartDate.isAfter(ticketDate)) {
        return PriceListStatus.AWAITING_START_DATE
      }
    }

    if (priceList.endAt) {
      const priceListEndDate = dayjs(priceList.endAt)

      if (priceListEndDate.isBefore(ticketDate)) {
        return PriceListStatus.EXPIRED
      }
    }

    if (!priceList.approval) {
      return PriceListStatus.AWAITING_APPROVAL
    }

    return PriceListStatus.APPROVED
  }

  const recalculateContractStatusForTicket = (contract: Contract, ticket: Pick<Ticket, "startAt" | "date">): ContractStatus => {
    const ticketDate = getActualTicketDate(ticket)

    if (contract.priceLists?.length && contract.priceLists?.every((priceList) => priceList.manuallyEnded)) {
      return ContractStatus.MANUALLY_ENDED
    }

    if (contract.startAt) {
      const contractStartDate = dayjs(contract.startAt)

      if (contractStartDate.isAfter(ticketDate)) {
        return ContractStatus.AWAITING_START_DATE
      }
    }

    if (contract.endAt) {
      const contractEndDate = dayjs(contract.endAt)

      if (contractEndDate.isBefore(ticketDate)) {
        return ContractStatus.EXPIRED
      }
    }

    const activePriceLists =
      contract.priceLists?.filter((priceList) =>
        [PriceListStatus.AWAITING_APPROVAL, PriceListStatus.APPROVED].includes(priceList.status),
      ) ?? []

    if (!activePriceLists.length) {
      return ContractStatus.NO_ACTIVE_PRICE_LISTS
    }

    if (activePriceLists.some((priceList) => priceList.status === PriceListStatus.AWAITING_APPROVAL)) {
      return ContractStatus.AWAITING_APPROVAL
    }

    if (activePriceLists.every((priceList) => priceList.status === PriceListStatus.APPROVED)) {
      return ContractStatus.APPROVED
    }

    return ContractStatus.PARTIALLY_APPROVED
  }

  const isContractValidForTicket = (
    contract: Contract,
    ticket: Partial<Pick<Ticket, "asset" | "supplier" | "organization">>,
    assetIdsOverride?: Array<string>,
  ): boolean => {
    if (contract.assets?.length) {
      if (assetIdsOverride) {
        if (!assetIdsOverride.every((asset) => contract.assets.some((contractAsset) => contractAsset === asset))) {
          return false
        }
      } else {
        if (!contract.assets.some((asset) => asset === ticket.asset?._id)) {
          return false
        }
      }
    }

    if (!(contract.supplier ? contract.supplier === ticket.supplier?._id : ticket.supplier?._id === ticket.organization)) {
      return false
    }

    if (ticket.organization !== contract.organization) {
      return false
    }

    return true
  }

  const getContractWithRecalculatedTicketStatuses = (
    contracts: Array<Contract>,
    ticket: Partial<Pick<Ticket, "startAt" | "date" | "supplier" | "organization" | "asset" | "type">>,
    assetIdsOverride?: Array<string>,
  ): Array<Contract> => {
    return contracts.map((contract): Contract => {
      if (!isContractValidForTicket(contract, ticket, assetIdsOverride)) {
        return {
          ...contract,
          priceLists: contract.priceLists?.map((priceList) => ({
            ...priceList,
            status: PriceListStatus.INVALID_FOR_TICKET,
          })),
          status: ContractStatus.INVALID_FOR_TICKET,
        }
      }

      const contractWithUpdatedPriceListStatus = {
        ...contract,
        priceLists: contract.priceLists?.map((priceList) => ({
          ...priceList,
          status: recalculatePriceListStatusForTicket(priceList, ticket),
        })),
      }

      return {
        ...contractWithUpdatedPriceListStatus,
        status: recalculateContractStatusForTicket(contractWithUpdatedPriceListStatus, ticket),
      }
    })
  }

  const getByPage = async <ReturnType = Contract>(queryParameters: Record<string, unknown> = {}): Promise<PaginateResult<ReturnType>> => {
    const query = getUrlSearchParams(queryParameters)

    const { data } = await useSamApi<PaginateResult<ReturnType>>(`${baseUrl}?${query}`)
    return data
  }

  const getById = async (id: string): Promise<ContractGetOneResult> => {
    const { data } = await useSamApi<ContractGetOneResult>(`${baseUrl}/${id}`)
    return data
  }

  const create = async (body: ContractCreate): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(baseUrl, {
      method: "POST",
      body: JSON.stringify(body),
    })

    triggerSummaryRefresh()

    return data
  }

  const update = async (id: string, body: ContractUpdate): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    })

    triggerSummaryRefresh()

    return data
  }

  const deleteById = async (id: string): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}`, { method: "DELETE" })

    return data
  }

  const createPriceList = async (id: string, priceList: Partial<PriceList>) => {
    const { data } = await useSamApi<PriceList>(`${baseUrl}/${id}/price-lists`, {
      method: "POST",
      body: JSON.stringify(priceList),
    })

    triggerSummaryRefresh()

    return data
  }

  const updatePriceList = async (id: string, priceListId: string, priceList: Partial<PriceList>) => {
    const { status, ...rest } = priceList
    const { data } = await useSamApi<PriceList>(`${baseUrl}/${id}/price-lists/${priceListId}`, {
      method: "PUT",
      body: JSON.stringify(rest),
    })

    triggerSummaryRefresh()

    return data
  }

  const endContract = async (id: string) => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}/end`, {
      method: "PATCH",
    })

    triggerSummaryRefresh()

    return data
  }

  const deletePriceList = async (id: string, priceListId: string) => {
    const { data } = await useSamApi<PriceList>(`${baseUrl}/${id}/price-lists/${priceListId}`, {
      method: "DELETE",
    })

    triggerSummaryRefresh()

    return data
  }

  const approvePriceList = async (id: string, priceListId: string) => {
    const { data } = await useSamApi<PriceList>(`${baseUrl}/${id}/price-lists/${priceListId}/approve`, {
      method: "POST",
    })

    triggerSummaryRefresh()
    return data
  }

  const denyPriceList = async (id: string, priceListId: string, remark: string) => {
    const { data } = await useSamApi<PriceList>(`${baseUrl}/${id}/price-lists/${priceListId}/deny`, {
      method: "POST",
      body: JSON.stringify({ remark }),
    })

    triggerSummaryRefresh()

    return data
  }

  const updateAssets = async (id: string, assets: Array<string>): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}/assets`, {
      method: "PUT",
      body: JSON.stringify(assets),
    })

    triggerSummaryRefresh()

    return data
  }

  const addRouteNumbers = async (id: string, routeNumbers: Array<RouteNumber>): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}/route-numbers`, {
      method: "POST",
      body: JSON.stringify({ routeNumbers }),
    })
    triggerSummaryRefresh()

    return data
  }

  const removeRouteNumber = async (id: string, routeNumber: RouteNumber): Promise<Contract> => {
    const { data } = await useSamApi<Contract>(`${baseUrl}/${id}/delete-route-numbers`, {
      method: "DELETE",
      body: JSON.stringify({ routeNumber }),
    })

    triggerSummaryRefresh()

    return data
  }

  const getSummary = async (): Promise<ContractsSummary> => {
    const { data } = await useSamApi<ContractsSummary>(`${baseUrl}/summary`)

    return data
  }

  return {
    refreshSummaryKey,
    getByPage,
    getById,
    getSummary,

    create,
    update,
    updateAssets,
    deleteById,

    createPriceList,
    updatePriceList,
    endContract,
    deletePriceList,
    approvePriceList,
    denyPriceList,
    addRouteNumbers,
    removeRouteNumber,

    getStoredContracts,
    // Return so it is stored but do not use it directly.
    storedContracts,

    getContractWithRecalculatedTicketStatuses,
  }
})
